import React, { useCallback, useEffect, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";
import { useField, ErrorMessage } from "formik";

import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  Quote,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Undo2,
  Redo2,
  Link as LinkIcon,
  Search,
  Plus,
} from "lucide-react";

const FormRichTextEditor = ({
  label,
  name,
  required,
  className = "",
  labelClassName = "",
}) => {
  const [field, meta, helpers] = useField(name);
  const { setValue, setTouched } = helpers;

  const [showFind, setShowFind] = useState(false);
  const [findText, setFindText] = useState("");
  const [replaceText, setReplaceText] = useState("");

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-primary underline font-medium cursor-pointer",
        },
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Placeholder.configure({
        placeholder: "Write something amazing...",
      }),
    ],
    content: field.value || "",
    onUpdate: ({ editor }) => {
      setValue(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: "p-6 min-h-[300px] focus:outline-none",
      },
    },
  });

  // Sync Formik -> Editor (important for resetForm)
  useEffect(() => {
    if (editor && field.value !== editor.getHTML()) {
      editor.commands.setContent(field.value || "");
    }
  }, [field.value, editor]);

  const setLink = useCallback(() => {
    if (!editor) return;

    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl);

    if (url === null) return;

    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  const handleFindReplace = () => {
    if (!findText || !editor) return;

    const currentHtml = editor.getHTML();
    const newHtml = currentHtml.split(findText).join(replaceText);

    editor.commands.setContent(newHtml);
    setShowFind(false);
  };

  if (!editor) return null;

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {label && (
        <label
          className={`text-fs_14 md:text-fs_16 font-fw_400 capitalize ${
            meta.touched && meta.error ? "text-errorColor" : labelClassName
          }`}
        >
          {label} {required && "*"}
        </label>
      )}

      <div
        onBlur={() => setTouched(true)}
        className={`relative border rounded-xl overflow-hidden transition-all ${
          meta.touched && meta.error ? "border-errorColor" : "border-greyText"
        } focus-within:ring-2 focus-within:ring-indigo-500/20`}
      >
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-1 p-2 bg-slate-50 border-b border-slate-200">
          {/* Heading */}
          <select
            className="h-8 bg-white border border-slate-200 rounded-md px-2 text-xs font-semibold"
            value={
              editor.isActive("heading", { level: 1 })
                ? "1"
                : editor.isActive("heading", { level: 2 })
                  ? "2"
                  : editor.isActive("heading", { level: 3 })
                    ? "3"
                    : editor.isActive("heading", { level: 4 })
                      ? "4"
                      : editor.isActive("heading", { level: 5 })
                        ? "5"
                        : editor.isActive("heading", { level: 6 })
                          ? "6"
                          : "p"
            }
            onChange={(e) => {
              const val = e.target.value;

              if (val === "p") {
                editor.chain().focus().setParagraph().run();
              } else {
                editor
                  .chain()
                  .focus()
                  .toggleHeading({ level: Number(val) })
                  .run();
              }
            }}
          >
            <option value="p">Paragraph</option>
            <option value="1">H1</option>
            <option value="2">H2</option>
            <option value="3">H3</option>
            <option value="4">H4</option>
            <option value="5">H5</option>
            <option value="6">H6</option>
          </select>

          {/* Bold */}
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={`p-1.5 rounded ${
              editor.isActive("bold")
                ? "bg-slate-200 text-primary"
                : "text-slate-600"
            }`}
          >
            <Bold size={16} />
          </button>

          {/* Italic */}
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={`p-1.5 rounded ${
              editor.isActive("italic")
                ? "bg-slate-200 text-primary"
                : "text-slate-600"
            }`}
          >
            <Italic size={16} />
          </button>

          {/* Underline */}
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={`p-1.5 rounded ${
              editor.isActive("underline")
                ? "bg-slate-200 text-primary"
                : "text-slate-600"
            }`}
          >
            <UnderlineIcon size={16} />
          </button>

          {/* Strike */}
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleStrike().run()}
            className={`p-1.5 rounded ${
              editor.isActive("strike")
                ? "bg-slate-200 text-primary"
                : "text-slate-600"
            }`}
          >
            <Strikethrough size={16} />
          </button>

          {/* Align */}
          <button
            type="button"
            onClick={() => editor.chain().focus().setTextAlign("left").run()}
          >
            <AlignLeft size={16} />
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().setTextAlign("center").run()}
          >
            <AlignCenter size={16} />
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().setTextAlign("right").run()}
          >
            <AlignRight size={16} />
          </button>

          {/* Lists */}
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <List size={16} />
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <ListOrdered size={16} />
          </button>

          {/* Quote */}
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
          >
            <Quote size={16} />
          </button>

          {/* Link */}
          <button type="button" onClick={setLink}>
            <LinkIcon size={16} />
          </button>

          {/* Find */}
          <button type="button" onClick={() => setShowFind(!showFind)}>
            <Search size={16} />
          </button>

          {/* Undo Redo */}
          <button
            type="button"
            onClick={() => editor.chain().focus().undo().run()}
          >
            <Undo2 size={16} />
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().redo().run()}
          >
            <Redo2 size={16} />
          </button>
        </div>

        {/* Find Replace */}
        {showFind && (
          <div className="bg-white border-b border-slate-200 p-3 flex gap-2">
            <input
              type="text"
              placeholder="Find..."
              value={findText}
              onChange={(e) => setFindText(e.target.value)}
              className="flex-1 border rounded px-2 py-1 text-sm"
            />
            <input
              type="text"
              placeholder="Replace..."
              value={replaceText}
              onChange={(e) => setReplaceText(e.target.value)}
              className="flex-1 border rounded px-2 py-1 text-sm"
            />
            <button
              type="button"
              onClick={handleFindReplace}
              className="bg-primary text-white px-3 rounded text-sm"
            >
              Replace
            </button>
          </div>
        )}

        {/* Editor */}
        <EditorContent editor={editor} />
      </div>

      {/* Error */}
      <label className="text-errorColor text-fs_14 ps-1 h-[1rem]">
        <ErrorMessage name={name} />
      </label>
    </div>
  );
};

export default FormRichTextEditor;
